import './settings.professional-caregiver.screen.scss';
import {useRouteMatch, Route, Switch, Redirect} from 'react-router-dom';
import {useAuth} from '../../../../../contexts/auth/context';
import {useOffice} from '../../../../../contexts/office/context';
import PermissionsRequiredRoute from '../../../../../components/permissions-required-route';
import {GenericCollapsibleSidePanel} from '../../../../../components/generic-collapsible-side-panel/generic-collapsible-side-panel';
import {InstitutionSidePanelComponents} from '../../../../../components/generic-collapsible-side-panel/institution-side-panel-components';
import InstitutionProfileProfessionalCaregiverScreen from './institution-profile/institution-profile.professional-caregiver.screen';
import InstitutionSettingsProfessionalCaregiverScreen from './institution-settings/institution-settings.professional-caregiver.screen';
import EntityActivitySectorSettings from './entity-activity-sector-settings/entity-activity-sector-settings.screen';
import LicensesProfessionalCaregiverOfficeScreen from './licenses/licenses.professional-caregiver.screen';
import {ScreenPaths} from '../../../../../enums';
import {RolePermissionType} from '../../../../../backend/enums/role-permission-type';
import {useMemo} from 'react';
import {useCanViewEntitySettings} from '../../../../../hooks/use-can-view-entity-settings';
import TemplatesProfessionalCaregiverOfficeScreen from './templates/templates.professional-caregiver.screen';
import {TemplateEditScreen} from './templates/templates-edit.professional-caregiver.screen';
import OccurrencesTypesProfessionalCaregiverOfficeScreen from './occurrences-types/occurrences-types.professional-caregiver.screen';
import QuickAccessProfessionalCaregiverSettingsOfficeScreen from './quick-access/quick-access.professional-caregiver.screen';
import AccessRequiredRoute from '../../../../../components/access-required-route';

export default function SettingsProfessionalCaregiverOfficeScreen() {
  const {hasReadPermission} = useAuth();
  const canViewEntitySettings = useCanViewEntitySettings();
  let {path, url} = useRouteMatch();
  let officeContext = useOffice();

  const showProfessionalCaregiverSettingsOptions = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Settings_Entity_Profile) ||
      canViewEntitySettings ||
      hasReadPermission(RolePermissionType.Settings_Licenses) ||
      hasReadPermission(RolePermissionType.Settings_BADL) ||
      hasReadPermission(RolePermissionType.Settings_IADL) ||
      hasReadPermission(RolePermissionType.Settings_ClinicalSupport) ||
      hasReadPermission(RolePermissionType.Settings_PersonalDevelopment) ||
      hasReadPermission(RolePermissionType.Settings_Templates) ||
      hasReadPermission(RolePermissionType.Settings_Occurrences) ||
      hasReadPermission(RolePermissionType.SettingsQuickAccessDevices),
    [hasReadPermission, canViewEntitySettings]
  );

  const defaultRoute = hasReadPermission(RolePermissionType.Settings_Entity_Profile)
    ? `${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Profile}`
    : canViewEntitySettings
      ? `${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Settings}`
      : hasReadPermission(RolePermissionType.Settings_Licenses)
        ? ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Licenses
        : hasReadPermission(RolePermissionType.Settings_Templates)
          ? ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates
          : hasReadPermission(RolePermissionType.Settings_Occurrences)
            ? ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Occurrences
            : hasReadPermission(RolePermissionType.SettingsQuickAccessDevices)
              ? ScreenPaths.ProfessionalCaregiver_InstitutionSettings_QuickAccessDevices
              : '';

  return (
    <div className="settings-dcm">
      {showProfessionalCaregiverSettingsOptions && (
        <GenericCollapsibleSidePanel>
          <InstitutionSidePanelComponents
            showExpanded={!officeContext.state.sideNavigationDrawerCollapsed}
            // hide activity sector settings
            activitiesSectors={[] /* activitiesSectors */}
          />
        </GenericCollapsibleSidePanel>
      )}
      <div className="main-panel-container">
        <Switch>
          <PermissionsRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Profile}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Settings_Entity_Profile,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.Settings_Entity_Profile`
              },
            ]}
          >
            <InstitutionProfileProfessionalCaregiverScreen />
          </PermissionsRequiredRoute>

          {/* Only available in testing entities while under development */}
          {canViewEntitySettings && (
            <PermissionsRequiredRoute
              exact
              path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Settings}`}
              requiredPermissions={[
                {
                  type: RolePermissionType.Settings_Entity_Settings,
                  canRead: true, // users must be able to have read permissions of `RolePermissionType.Settings_Entity_Settings`
                },
              ]}
            >
              <InstitutionSettingsProfessionalCaregiverScreen />
            </PermissionsRequiredRoute>
          )}

          <PermissionsRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Licenses}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Settings_Licenses,
                canRead: true, // users must be able to have read permissions of `RolePermissionType.Settings_Licenses`
              },
            ]}
          >
            <LicensesProfessionalCaregiverOfficeScreen />
          </PermissionsRequiredRoute>

          <Route exact path={`${path}${ScreenPaths.ProfessionalCaregiver_ActivitySectorSettings}`}>
            <EntityActivitySectorSettings />
          </Route>

          <Route path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`}>
            <Switch>
              <PermissionsRequiredRoute
                exact
                path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Settings_Templates,
                    canRead: true, // users must be able to have read permissions of `RolePermissionType.Settings_Templates`
                  },
                ]}
              >
                <TemplatesProfessionalCaregiverOfficeScreen />
              </PermissionsRequiredRoute>

              <PermissionsRequiredRoute
                exact
                path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_TemplatesEdit}`}
                requiredPermissions={[
                  {
                    type: RolePermissionType.Settings_Templates,
                    canRead: true, // users must be able to have read permissions of `RolePermissionType.Settings_Templates`
                  },
                ]}
              >
                <TemplateEditScreen />
              </PermissionsRequiredRoute>
            </Switch>
            {/*  Default route templates */}
            <Route path={path}>
              <Redirect
                to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`}
              />
            </Route>
          </Route>

          <AccessRequiredRoute
            access="care_management"
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Occurrences}`}
            requiredPermissions={[
              {
                type: RolePermissionType.Settings_Occurrences,
                canRead: true,
              },
            ]}
          >
            <OccurrencesTypesProfessionalCaregiverOfficeScreen />
          </AccessRequiredRoute>

          <PermissionsRequiredRoute
            exact
            path={`${path}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_QuickAccessDevices}`}
            requiredPermissions={[
              {
                type: RolePermissionType.SettingsQuickAccessDevices,
                canRead: true,
              },
            ]}
          >
            <QuickAccessProfessionalCaregiverSettingsOfficeScreen />
          </PermissionsRequiredRoute>

          {/* Default */}
          <Route path={path}>
            <Redirect to={`${url}${defaultRoute}`} />
          </Route>
        </Switch>
      </div>
    </div>
  );
}
